import { isAsyncCheckOptedIn } from '../../form-config/webhook-rules.js';
/** Split the shared meta (name/stage/severity/override) out of a step's config; the rest is the type-config. */
function splitMeta(config) {
    const { name = '', stage, stages, severity = 'error', overrideBehavior, overrideConfig, ...rest } = config;
    return {
        name: name, stage: stage,
        stages: Array.isArray(stages) && stages.length > 0 ? stages : undefined,
        severity: severity,
        overrideBehavior, overrideConfig, rest,
    };
}
function recombine(rule, typeConfig) {
    return {
        name: rule.name ?? '', stage: rule.stage,
        ...(rule.stages !== undefined ? { stages: rule.stages } : {}),
        severity: rule.severity,
        overrideBehavior: rule.overrideBehavior, overrideConfig: rule.overrideConfig,
        ...typeConfig,
    };
}
/** E-032 back-compat emission — stages[] authoritative when present, `stage`
 *  stays populated (first selected) for readers of the old field. ONE home for
 *  the rule so all three check compilers share it. */
function stageMeta(m) {
    return {
        stage: m.stages?.[0] ?? m.stage ?? 'verification',
        ...(m.stages ? { stages: m.stages } : {}),
    };
}
/**
 * Config-based Check effects (uniqueness / historicalRange / webhook): field-scope,
 * fieldRef mandatory, the type-config lives on rule.config. `moment` is the effect's
 * canonical moment returned by parse (trigger is always on-validate — server-data
 * checks evaluate at the batch gate regardless).
 */
export function makeConfigCheck(effectKey, ruleType, moment) {
    const compile = (step) => {
        const { name, stage, stages, severity, overrideBehavior, overrideConfig, rest } = splitMeta(step.config);
        return {
            name, type: ruleType, scope: 'field', trigger: 'on-validate',
            ...stageMeta({ stage, stages }),
            severity, overrideBehavior, overrideConfig, config: rest,
            fieldRef: step.fieldName, condition: step.condition,
        };
    };
    const parse = (payload) => {
        if (!payload || typeof payload !== 'object')
            return null;
        const rule = payload;
        if (rule.type !== ruleType)
            return null;
        return {
            effectKey, moment, scope: 'field',
            fieldName: rule.fieldRef ?? undefined,
            condition: rule.condition,
            config: recombine(rule, rule.config ?? {}),
        };
    };
    return { compile, parse };
}
export { splitMeta, recombine, stageMeta };
/**
 * validate_check: the live path is a single actions[] entry of type 'validate'
 * carrying the ValidateRuleConfig (collectValidateBindings reads rule.actions).
 */
export const validateCheck = {
    compile: (step) => {
        const { name, stage, stages, severity, overrideBehavior, overrideConfig, rest } = splitMeta(step.config);
        return {
            name, type: 'validate', scope: 'field', trigger: 'on-validate',
            ...stageMeta({ stage, stages }),
            severity, overrideBehavior, overrideConfig, config: {},
            fieldRef: step.fieldName, condition: step.condition,
            actions: [{ id: 'validate-0', type: 'validate', config: rest, displayOrder: 0 }],
        };
    },
    parse: (payload) => {
        if (!payload || typeof payload !== 'object')
            return null;
        const rule = payload;
        if (rule.type !== 'validate')
            return null;
        return {
            effectKey: 'validate_check', moment: 'on_validate', scope: 'field',
            fieldName: rule.fieldRef ?? undefined,
            condition: rule.condition,
            config: recombine(rule, rule.actions?.[0]?.config ?? {}),
        };
    },
};
/**
 * Task 3 (E-062) — webhook_check threads its S-5 dial timing through the
 * moment. Built ON makeConfigCheck (same meta/config plumbing) with two
 * overrides:
 *
 *  - compile: moment → trigger (keying→on-change, on_validate→on-validate,
 *    step_enter→on-step-enter; batch_complete keeps the legacy gate-only
 *    'on-validate' shape). A dial moment whose config carries none of the
 *    S-5 opt-in keys gets `asyncCheck: true` stamped so the authored timing
 *    actually dials (collectWebhookChecksForTiming's opt-in gate).
 *  - parse: the moment shown is the moment that EXECUTES — opted-in rules
 *    recover it from the trigger; non-opted-in (legacy) rules are gate-only
 *    and always parse to batch_complete, whatever their stored trigger says.
 */
const WEBHOOK_TRIGGER_FOR_MOMENT = {
    keying: 'on-change',
    on_validate: 'on-validate',
    step_enter: 'on-step-enter',
};
const WEBHOOK_MOMENT_FOR_TRIGGER = {
    'on-change': 'keying',
    'on-validate': 'on_validate',
    'on-step-enter': 'step_enter',
};
export const webhookCheck = (() => {
    const base = makeConfigCheck('webhook_check', 'webhook', 'batch_complete');
    return {
        compile: (step) => {
            const out = base.compile(step);
            const dialTrigger = WEBHOOK_TRIGGER_FOR_MOMENT[step.moment];
            if (!dialTrigger)
                return out; // batch_complete — legacy gate-only shape
            const config = out.config;
            const optedIn = config['severity'] !== undefined || config['onUnresolved'] !== undefined ||
                config['transport'] !== undefined || config['timeoutMs'] !== undefined ||
                config['asyncCheck'] === true;
            return {
                ...out,
                trigger: dialTrigger,
                ...(optedIn ? {} : { config: { ...config, asyncCheck: true } }),
            };
        },
        parse: (payload) => {
            const step = base.parse(payload);
            if (!step)
                return null;
            const rule = payload;
            if (!isAsyncCheckOptedIn(rule))
                return step; // gate-only → batch_complete
            return { ...step, moment: WEBHOOK_MOMENT_FOR_TRIGGER[String(rule.trigger)] ?? 'batch_complete' };
        },
    };
})();
/**
 * cross_field_check: condition-based (evaluateConditionRule reads rule.condition).
 * Form scope by default, batch when the step is batch-scoped. No fieldRef.
 * The condition is REQUIRED at author time (design §5) — enforced by the editor;
 * compile carries whatever is present.
 */
export const crossFieldCheck = {
    compile: (step) => {
        const { name, stage, stages, severity, overrideBehavior, overrideConfig, rest } = splitMeta(step.config);
        return {
            name, type: 'crossField',
            scope: step.scope === 'batch' ? 'batch' : 'form',
            trigger: 'on-validate',
            ...stageMeta({ stage, stages }),
            severity,
            overrideBehavior, overrideConfig, config: rest, condition: step.condition,
        };
    },
    parse: (payload) => {
        if (!payload || typeof payload !== 'object')
            return null;
        const rule = payload;
        if (rule.type !== 'crossField')
            return null;
        return {
            // R0 truth: crossField is evaluated only by the batch gate — parse to
            // the moment it actually fires at (was the false 'on_validate' claim).
            effectKey: 'cross_field_check', moment: 'batch_complete',
            scope: rule.scope ?? 'form',
            condition: rule.condition,
            config: recombine(rule, rule.config ?? {}),
        };
    },
};
//# sourceMappingURL=check-effect.js.map